import { useCallback, useMemo } from 'react';

import { useTranslate } from '@src/core/hooks';
import { isVoucherPending } from '@src/domains/vouchers/core/isVoucherPending';
import { buildVoucherBlocks, VoucherBlockLayout, VoucherState, type Voucher } from '@src/domains/vouchers';
import { useReservationVoucherDownload } from '@src/features/vouchers/download';
import { VOUCHER_STATE_LABEL_KEYS } from '@src/features/vouchers/list/utils/voucherStateLabelKeys';
import { track } from '@src/services/analytics';

/** Pending vouchers have no usable code yet, so the columns are masked like in the legacy table. */
const MASKED_VALUE = '-';

export interface VoucherBlockView {
  key: string;
  tariffsLabel: string;
  code: string;
  secret: string;
  statusLabel: string;
  isDownloadable: boolean;
  voucher: Voucher;
}

interface Params {
  vouchers: Voucher[];
  reservationId: string | null;
}

export function useReservationVouchersModal(params: Params) {
  const { vouchers, reservationId } = params;
  const { i18n } = useTranslate();

  const { layout, blocks, showsDownloadAll } = useMemo(() => buildVoucherBlocks(vouchers), [vouchers]);
  const hasPendingVouchers = useMemo(() => vouchers.some(isVoucherPending), [vouchers]);

  const { downloadAll, downloadOne, isDownloadingAll, isDownloadingOne, hasDownloadableTargets } =
    useReservationVoucherDownload({
      reservationId,
      vouchers,
    });

  const blockViews: VoucherBlockView[] = useMemo(
    () =>
      blocks.map((block) => {
        const isPending = block.state === VoucherState.Pending;

        return {
          key: block.key,
          tariffsLabel: block.tariffs
            .map((tariff) =>
              i18n('checkout_vouchers_modal_tariff_quantity', { count: tariff.quantity, name: tariff.name }),
            )
            .join(', '),
          code: isPending ? MASKED_VALUE : block.voucher.code,
          secret: isPending ? MASKED_VALUE : block.voucher.secret,
          statusLabel: i18n(VOUCHER_STATE_LABEL_KEYS[block.state]),
          isDownloadable: block.isDownloadable,
          voucher: block.voucher,
        };
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- i18n is stable per locale
    [blocks],
  );

  const trackedDownloadOne = useCallback(
    (voucher: Voucher) => {
      track('saleViewVoucher', { saleItemId: voucher.saleItemId });

      return downloadOne(voucher);
    },
    [downloadOne],
  );

  const trackedDownloadAll = useCallback(() => {
    const [first] = vouchers;

    if (first) {
      track('saleViewVoucher', { saleItemId: first.saleItemId });
    }

    return downloadAll();
  }, [downloadAll, vouchers]);

  return {
    layout,
    blocks: blockViews,
    showsDownloadAll,
    hasPendingVouchers,
    hasDownloadableTargets,
    singleVoucher: layout === VoucherBlockLayout.Combined ? vouchers[0] ?? null : null,
    downloadAll: trackedDownloadAll,
    downloadOne: trackedDownloadOne,
    isDownloadingAll,
    isDownloadingOne,
  };
}

export default useReservationVouchersModal;
